/**
 * Estado y turno de un día en Asistencia (008 · T097; contracts/ui.md §2.2, design-system §7
 * "Badge de estado"). Bajo el `h2` del día: badge con icono **y** texto (14 px, nunca solo color) y
 * "Turno 08:00 – 17:00" (14 px, neutro 600). El estado lo decide el servidor (`display_status`);
 * `statusBadgeOf` solo elige texto, icono y tono. Un día "En curso" (`PENDING`) nunca se muestra
 * como falta (US6-5).
 *
 * 008 · T111 (US7, contracts/ui.md §2.2): bajo el badge, la acción "Justificar" cuando
 * `justify.kinds` no está vacío (lo decide el servidor; nunca con la empresa suspendida) y, si el
 * trabajador ya envió una del día, "Justificación en revisión" (spec US7-2). Sin `pendingKinds`,
 * `readOnly` ni `online`, se pinta como en la T097.
 */
import { Hourglass } from 'lucide-react';

import type { JustificationKind, StaffDay } from '@fermin/contracts';

import { cn } from '../../components/ui/cn';
import { t } from '../../i18n';
import { JustifyAction } from '../justification/justify-action';

import { shiftLineOf, statusBadgeOf, type StatusTone } from './format';

const TONE_CLASS: Record<StatusTone, string> = {
  success: 'bg-fm-success-soft text-fm-success-text',
  warning: 'bg-fm-warning-soft text-fm-warning-text',
  danger: 'bg-fm-danger-soft text-fm-danger-text',
  info: 'bg-fm-info-soft text-fm-info-text',
  neutral: 'bg-fm-bg-sunken text-fm-text',
};

export interface DayStatusProps {
  day: StaffDay;
  /** 008 · T111: tipos con una justificación `PENDING` de este día (de `GET …/justifications`). */
  pendingKinds?: readonly JustificationKind[];
  /** Empresa suspendida: sin "Justificar". */
  readOnly?: boolean;
  online?: boolean;
}

export function DayStatus({ day, pendingKinds, readOnly = false, online = true }: DayStatusProps) {
  const badge = statusBadgeOf(day);
  const shiftLine = shiftLineOf(day);
  const inReview = (pendingKinds?.length ?? 0) > 0;
  const canJustify = !readOnly && day.justify.kinds.length > 0;
  if (badge === null && shiftLine === null && !inReview && !canJustify) return null;
  const Icon = badge?.icon;
  const statusRow = (
    <div className="flex flex-wrap items-center gap-x-fm-3 gap-y-fm-1">
      {badge !== null && Icon !== undefined ? (
        <span
          data-testid="day-status"
          data-status={badge.status}
          className={cn(
            'inline-flex w-fit items-center gap-fm-1 rounded-fm-pill px-fm-2 py-0.5 font-ui text-fm-sm font-medium',
            TONE_CLASS[badge.tone],
          )}
        >
          <Icon aria-hidden="true" size={14} />
          {badge.text}
        </span>
      ) : null}
      {shiftLine !== null ? (
        <span
          data-testid="day-shift"
          className="fm-tabular text-fm-sm font-semibold text-fm-text-secondary"
        >
          {shiftLine}
        </span>
      ) : null}
      {inReview ? (
        <span
          data-testid="day-justification"
          data-status="PENDING"
          className="inline-flex w-fit items-center gap-fm-1 rounded-fm-pill bg-fm-info-soft px-fm-2 py-0.5 font-ui text-fm-sm font-medium text-fm-info-text"
        >
          <Hourglass aria-hidden="true" size={14} />
          {t('justification.action.inReview')}
        </span>
      ) : null}
    </div>
  );
  if (!canJustify) return statusRow;
  return (
    <div className="flex flex-col gap-fm-1">
      {statusRow}
      <JustifyAction day={day} readOnly={readOnly} online={online} />
    </div>
  );
}
